import type { SupervisorAuditLabelRequest, SupervisorProposalCorrect } from '@avisoma/shared';
import { useState } from 'react';
import { Button } from '../../catalyst/button';
import { Field, Label } from '../../catalyst/fieldset';
import { Textarea } from '../../catalyst/textarea';
import { auditRequest, initialAuditForm, proposalChoiceEnabled, withDigitKey, type AuditForm } from './auditForm';
import { AUDIT_SHORTCUT_HINT } from './auditPresentation';
import { ToggleGroup, type ToggleOption } from './ToggleGroup';
import { useAuditShortcuts } from './useAuditShortcuts';

const MAX_NOTE = 2000;
const yesNo = (yes: string, no: string): ToggleOption<boolean>[] => [
  { value: true, label: 'Yes', hotkey: yes },
  { value: false, label: 'No', hotkey: no },
];
const PROPOSAL_OPTIONS: ToggleOption<SupervisorProposalCorrect>[] = [
  { value: 'yes', label: 'Yes', hotkey: '3' },
  { value: 'no', label: 'No', hotkey: '4' },
  { value: 'not_applicable', label: 'Not applicable', hotkey: '5' },
];

/**
 * The three answers, the note, Submit and Skip for one finding. Key it by the finding id so each item starts empty.
 * Skip moves on without a label, for a finding the reviewer cannot or should not label.
 */
export function AuditLabelForm({
  hasProposal,
  busy,
  onSubmit,
  onSkip,
}: {
  hasProposal: boolean;
  busy: boolean;
  onSubmit: (body: SupervisorAuditLabelRequest) => void;
  onSkip: () => void;
}) {
  const [form, setForm] = useState<AuditForm>(() => initialAuditForm(hasProposal));
  const request = auditRequest(form);
  const submit = () => {
    if (request && !busy) onSubmit(request);
  };
  useAuditShortcuts({ digit: (key) => setForm((f) => withDigitKey(f, key, hasProposal)), submit });
  const proposalOptions = PROPOSAL_OPTIONS.map((o) => ({ ...o, disabled: !proposalChoiceEnabled(hasProposal, o.value) }));
  return (
    <div className="space-y-4">
      <ToggleGroup label="Real discrepancy" options={yesNo('1', '2')} value={form.realDiscrepancy} onChange={(v) => setForm((f) => ({ ...f, realDiscrepancy: v }))} />
      <ToggleGroup label="Proposed value correct" options={proposalOptions} value={form.proposalCorrect} onChange={(v) => setForm((f) => ({ ...f, proposalCorrect: v }))} />
      <ToggleGroup label="Useful to a lessor" options={yesNo('6', '7')} value={form.usefulToLessor} onChange={(v) => setForm((f) => ({ ...f, usefulToLessor: v }))} />
      <Field>
        <Label>Note</Label>
        <Textarea rows={3} maxLength={MAX_NOTE} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
      </Field>
      <div className="flex flex-wrap items-center gap-4">
        <Button color="indigo" disabled={!request || busy} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit'}
        </Button>
        <Button outline disabled={busy} onClick={onSkip}>
          Skip
        </Button>
        <p className="text-xs text-gray-500 dark:text-zinc-400">{AUDIT_SHORTCUT_HINT}</p>
      </div>
    </div>
  );
}
